/* eslint max-lines: ["error", {max: 332}] -- custom hook reduces repetition */
/* eslint max-statements: ["error", {max: 24}] -- custom hook reduces statements */
/* eslint max-lines-per-function: ["error", {max: 206}] -- custom hook reduces function size */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { useEvent } from "expo";
import type {
  PlaybackRateChangeEventPayload,
  PlayingChangeEventPayload,
  SourceLoadEventPayload,
  TimeUpdateEventPayload,
  VideoContentFit,
  VideoPlayer,
} from "expo-video";
import { useVideoPlayer, VideoView } from "expo-video";
import isNil from "lodash/isNil";
import type { ViewStyle } from "react-native";

import { Image } from "@/ui/core";
import { Box } from "@/ui/layout";

// ExpoVideoPlayer
//  - Adapter layer to migrate from expo-av <Video> to expo-video <VideoView>
//  - Exposes legacy-like imperative API expected by existing code:
//      playAsync(), pauseAsync(), getStatusAsync(), unloadAsync(), setRateAsync()
//  - Synthesizes an AVPlaybackStatusSuccess-shaped object for downstream watch tracking & analytics layers
//  - Maps resize modes & thumbnail (poster) behavior
//  - Provides progress update events based on player.timeUpdateEventInterval (ms -> seconds)

// Shape mimicking fields actually consumed in existing code (subset of AVPlaybackStatusSuccess)
interface VideoPlaybackStatus {
  readonly durationMillis: number;
  readonly isLoaded: true;
  readonly isPlaying: boolean;
  readonly positionMillis: number;
  readonly rate: number;
}

type OnVideoStatusUpdate = (status: VideoPlaybackStatus) => void;

interface VideoNaturalSize {
  height: number;
  width: number;
}

type PlaybackRateChangePayload = Pick<VideoPlayer, "currentTime"> &
  PlaybackRateChangeEventPayload;

type PlayingChangePayload = Pick<VideoPlayer, "currentTime" | "duration"> &
  PlayingChangeEventPayload;

type TimeUpdatePayload = {
  isPlaying: VideoPlayer["playing"];
} & Pick<TimeUpdateEventPayload, "currentTime">;

interface ExpoVideoPlayerProps extends ViewWithTestID {
  contentFit?: VideoContentFit;
  onError: (error: string) => void;
  onFirstFrame?: () => void;
  onPlaybackRateChange?: (payload: PlaybackRateChangePayload) => void;
  onPlayingChange?: (payload: PlayingChangePayload) => void;
  onSourceLoad?: (payload: SourceLoadEventPayload) => void;
  onTimeUpdate?: (payload: TimeUpdatePayload) => void;
  onVideoSize?: (size: VideoNaturalSize) => void;
  onVideoStatusUpdate?: OnVideoStatusUpdate;
  progressUpdateIntervalMillis: number;
  shouldAutoPlay?: boolean;
  shouldShowControls?: boolean;
  shouldUseThumbnail?: boolean;
  sourceUri: string;
  style?: ViewStyle;
  thumbnailUri?: string;
}

interface ExpoVideoPlayerHandle {
  getStatusAsync: () => Promise<VideoPlaybackStatus>;
  pauseAsync: () => Promise<void>;
  playAsync: () => Promise<void>;
  setRateAsync: (rate: number) => Promise<void>;
  unloadAsync: () => Promise<void>;
}

const toMillis = (seconds?: number): number => {
  if (!seconds || Number.isNaN(seconds)) return 0;
  return Math.round(seconds * 1000);
};

const useEmitVideoSize = ({
  onVideoSize,
  videoTrack,
}: {
  onVideoSize?: (size: VideoNaturalSize) => void;
  videoTrack: { size: VideoNaturalSize } | null;
}) => {
  const naturalWidth = videoTrack?.size.width ?? 0;
  const naturalHeight = videoTrack?.size.height ?? 0;
  useEffect(() => {
    if (naturalWidth <= 0 || naturalHeight <= 0) return;
    onVideoSize?.({ height: naturalHeight, width: naturalWidth });
  }, [naturalHeight, naturalWidth, onVideoSize]);
};

const ExpoVideoPlayer = forwardRef<ExpoVideoPlayerHandle, ExpoVideoPlayerProps>(
  (
    // eslint-disable-next-line no-restricted-syntax -- this component relies on multiple props related to video tracking
    {
      contentFit = "contain",
      onError,
      onFirstFrame,
      onPlaybackRateChange,
      onPlayingChange,
      onSourceLoad,
      onTimeUpdate,
      onVideoSize,
      onVideoStatusUpdate,
      progressUpdateIntervalMillis,
      shouldAutoPlay = false,
      shouldShowControls = true,
      shouldUseThumbnail = false,
      sourceUri,
      style,
      testID,
      thumbnailUri,
    },
    ref,
    // eslint-disable-next-line max-params
  ) => {
    const player = useVideoPlayer(sourceUri, (playerInstance) => {
      const intervalSeconds = progressUpdateIntervalMillis / 1000;
      // eslint-disable-next-line no-param-reassign -- intentional configuration of player
      playerInstance.timeUpdateEventInterval = intervalSeconds;
      if (shouldAutoPlay) playerInstance.play();
    });

    const [isFirstFrameRendered, setIsFirstFrameRendered] = useState(false);

    const markFirstFrame = useCallback(() => {
      if (isFirstFrameRendered) return;
      setIsFirstFrameRendered(true);
      onFirstFrame?.();
    }, [isFirstFrameRendered, onFirstFrame]);

    // Subscribe to events to synthesize status object
    // Event subscriptions keep React aware of player changes; values unused directly but trigger re-renders.
    const sourceLoad = useEvent(player, "sourceLoad");
    const { isPlaying } = useEvent(player, "playingChange", {
      isPlaying: player.playing,
    });
    const { playbackRate } = useEvent(player, "playbackRateChange", {
      playbackRate: player.playbackRate,
    });
    const { error: statusError } = useEvent(player, "statusChange", {
      status: player.status,
    });
    // currentTime from TimeUpdateEventPayload is updated every timeUpdateEventInterval,
    const { currentTime } = useEvent(player, "timeUpdate", {
      bufferedPosition: player.bufferedPosition,
      currentLiveTimestamp: player.currentLiveTimestamp,
      currentOffsetFromLive: player.currentOffsetFromLive,
      currentTime: player.currentTime,
    });
    // videoTrack.size carries the natural pixel dimensions of the video; null on web (expo-video web stub)
    const { videoTrack } = useEvent(player, "videoTrackChange", {
      videoTrack: player.videoTrack,
    });
    useEmitVideoSize({ onVideoSize, videoTrack });

    // Fire change callbacks when native events occur
    useEffect(() => {
      if (!sourceLoad) return;
      onSourceLoad?.(sourceLoad);
    }, [onSourceLoad, sourceLoad]);

    const previousCurrentTimeRef = useRef<number | undefined>(undefined);
    useEffect(() => {
      if (previousCurrentTimeRef.current === currentTime) return;
      if (previousCurrentTimeRef.current !== undefined) {
        onTimeUpdate?.({
          currentTime,
          isPlaying: player.playing,
        });
      }
      previousCurrentTimeRef.current = currentTime;
    }, [currentTime, onTimeUpdate, player.playing]);

    const previousIsPlayingRef = useRef<boolean | undefined>(undefined);
    useEffect(() => {
      if (previousIsPlayingRef.current === isPlaying) return;
      if (previousIsPlayingRef.current !== undefined) {
        onPlayingChange?.({
          // player.currentTime is more up-to-date than currentTime from TimeUpdateEventPayload
          currentTime: player.currentTime,
          // web does not fire onSourceLoad, so we need to capture video length on initial play
          duration: player.duration,
          isPlaying,
        });
      }
      previousIsPlayingRef.current = isPlaying;
    }, [isPlaying, onPlayingChange, player.currentTime, player.duration]);

    const previousPlaybackRateRef = useRef<number | undefined>(undefined);
    useEffect(() => {
      if (previousPlaybackRateRef.current === playbackRate) return;
      if (previousPlaybackRateRef.current !== undefined) {
        onPlaybackRateChange?.({
          // player.currentTime is more up-to-date than currentTime from TimeUpdateEventPayload
          currentTime: player.currentTime,
          playbackRate,
        });
      }
      previousPlaybackRateRef.current = playbackRate;
    }, [playbackRate, onPlaybackRateChange, player.currentTime]);

    useEffect(() => {
      if (isNil(statusError)) return;
      onError(statusError.message);
    }, [onError, statusError]);

    // Compute derived fields (duration accessible via player.duration)
    const currentStatus: null | VideoPlaybackStatus = useMemo(() => {
      if (player.status !== "readyToPlay") return null;
      return {
        durationMillis: toMillis(player.duration),
        isLoaded: true,
        isPlaying: player.playing,
        positionMillis: toMillis(player.currentTime),
        rate: player.playbackRate,
      };
    }, [
      player.status,
      player.playing,
      player.duration,
      player.currentTime,
      player.playbackRate,
    ]);

    // Emit status updates similar to expo-av onPlaybackStatusUpdate frequency: triggered whenever underlying dependencies change.
    const lastEmittedStatusRef = useRef<null | VideoPlaybackStatus>(null);
    useEffect(() => {
      if (!currentStatus) return;
      markFirstFrame();
      const previousStatus = lastEmittedStatusRef.current;
      const hasStatusChanged =
        previousStatus?.isPlaying !== currentStatus.isPlaying ||
        previousStatus.positionMillis !== currentStatus.positionMillis ||
        previousStatus.rate !== currentStatus.rate;
      if (!hasStatusChanged) return;
      lastEmittedStatusRef.current = currentStatus;
      onVideoStatusUpdate?.(currentStatus);
    }, [currentStatus, onVideoStatusUpdate, markFirstFrame]);

    // Imperative handle
    useImperativeHandle(
      ref,
      (): ExpoVideoPlayerHandle => ({
        getStatusAsync: async () => {
          // Fallback to synthesized status; always coerce isLoaded true (legacy consumer only handles loaded branch)
          if (currentStatus) return currentStatus;
          const statusSnapshot: VideoPlaybackStatus = {
            durationMillis: toMillis(player.duration),
            isLoaded: true,
            isPlaying: player.playing,
            positionMillis: toMillis(player.currentTime),
            rate: player.playbackRate,
          };
          return statusSnapshot;
        },
        pauseAsync: async () => {
          player.pause();
        },
        playAsync: async () => {
          player.play();
        },
        setRateAsync: async (rate: number) => {
          player.playbackRate = rate;
        },
        unloadAsync: async () => {
          // useVideoPlayer cleans up automatically; emulate async API
        },
      }),
      [player, currentStatus],
    );

    // Poster/thumbnail overlay logic
    const shouldShowPoster =
      shouldUseThumbnail && !isFirstFrameRendered && Boolean(thumbnailUri);

    return (
      <Box style={style} testID={testID}>
        <VideoView
          contentFit={contentFit}
          // eslint-disable-next-line @typescript-eslint/naming-convention
          fullscreenOptions={{ enable: true }}
          nativeControls={shouldShowControls}
          // @ts-expect-error: cross-platform typing mismatch (web composite vs native); runtime compatible
          player={player}
          style={style}
        />
        {shouldShowPoster && (
          <Image
            contentFit={contentFit}
            size={{ height: 100, width: 100 }}
            testID="expo-video-player-poster"
            uriSource={{ uri: thumbnailUri }}
          />
        )}
      </Box>
    );
  },
);

ExpoVideoPlayer.displayName = "ExpoVideoPlayer";

export { ExpoVideoPlayer };
export type {
  ExpoVideoPlayerHandle,
  ExpoVideoPlayerProps,
  PlaybackRateChangePayload,
  PlayingChangePayload,
  TimeUpdatePayload,
  VideoNaturalSize,
  VideoPlaybackStatus,
};
